import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Progress,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import SyncProtocolScheduler from './SyncProtocolScheduler';
import SyncProtocolWrittenGuide from './SyncProtocolWrittenGuide';
import {
  countCompletedSteps,
  getSyncCompletionPercent,
} from '../utils/syncProtocolUtils';
import {
  buildSyncProgramPayload,
  endSyncProgram,
  fetchAnimalSyncPrograms,
  updateSyncProgram,
} from '../services/syncProgramService';
import '../styles/breeding-form.css';
import '../styles/sync-programs.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

const SPECIES_KEYS = ['cattle', 'buffalo', 'sheep', 'goat', 'camel', 'horse', 'donkey'];

function getSpeciesKeyFromAnimal(animal) {
  const cat = animal?.category;
  if (!cat) return null;
  const key = ((cat.slug || cat.name || '')).toLowerCase().trim();
  return SPECIES_KEYS.includes(key) ? key : null;
}

function animalTitle(animal) {
  if (!animal) return 'Animal';
  return animal.name || animal.tag_number || `Animal #${animal.id}`;
}

export default function SyncProgramModal({
  open,
  animalId,
  programId,
  mode = 'view',
  onClose,
  onSaved,
}) {
  const [panelMode, setPanelMode] = useState(mode);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { can, isSuperAdmin } = usePermissions();
  const canEdit = can('edit animal breeding') || isSuperAdmin;

  useEffect(() => {
    if (open) {
      setPanelMode(mode === 'manage' ? 'manage' : 'view');
      form.resetFields();
    }
  }, [open, mode, programId, animalId, form]);

  const { data: animal, isLoading: animalLoading } = useQuery({
    queryKey: ['animal-basic', animalId],
    queryFn: async () => {
      const res = await api.get(`/animals/${animalId}/basic-info`);
      return res.data.data || res.data;
    },
    enabled: open && Boolean(animalId),
  });

  const { data: programs = [], isLoading: programsLoading } = useQuery({
    queryKey: ['animal-sync-programs', animalId],
    queryFn: () => fetchAnimalSyncPrograms(animalId),
    enabled: open && Boolean(animalId),
  });

  const program = useMemo(
    () => programs.find((row) => String(row.id) === String(programId)) || null,
    [programs, programId]
  );

  const completion = program ? getSyncCompletionPercent(program.sync_protocol_data) : null;
  const isComplete = completion != null && completion >= 100;
  const endedAt = program?.sync_protocol_data?.endedAt || null;
  const hasOpenSteps = (program?.sync_protocol_data?.steps || [])
    .some((step) => !['done', 'cancelled'].includes(step.status || 'pending'));
  const loading = animalLoading || programsLoading;

  useEffect(() => {
    if (!open || panelMode !== 'manage' || !program) return;
    form.setFieldsValue({
      sync_protocol_data: program.sync_protocol_data || null,
      notes: program.notes,
    });
  }, [open, panelMode, program, form]);

  const saveMutation = useMutation({
    mutationFn: (values) => {
      const payload = buildSyncProgramPayload(values, { programId: program?.id });
      return updateSyncProgram(animalId, program.id, payload);
    },
    onSuccess: async () => {
      message.success('Sync program updated');
      await queryClient.invalidateQueries({ queryKey: ['animal-sync-programs', animalId] });
      if (onSaved) await onSaved();
      setPanelMode('view');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update sync program');
    },
  });

  const endMutation = useMutation({
    mutationFn: (reason) => endSyncProgram(program.id, reason),
    onSuccess: async (result) => {
      message.success(result?.message || 'Program ended');
      await queryClient.invalidateQueries({ queryKey: ['animal-sync-programs', animalId] });
      if (onSaved) await onSaved();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to end the program');
    },
  });

  const confirmEnd = () => {
    let reason = '';
    Modal.confirm({
      title: 'End this program?',
      content: (
        <div>
          <p>The steps not given yet are cancelled and drop off the calendar and reminders. Steps already done, and the stock they used, stay as they are.</p>
          <TextArea
            rows={2}
            maxLength={500}
            placeholder="Reason (optional), e.g. came on heat early"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'End program',
      okButtonProps: { danger: true },
      onOk: () => endMutation.mutateAsync(reason),
    });
  };

  const handleClose = () => {
    form.resetFields();
    setPanelMode('view');
    onClose();
  };

  const renderViewPanel = () => {
    if (!program) {
      return (
        <Empty description="Sync program not found" />
      );
    }

    return (
      <div className={`sp-program-modal-view${isComplete ? ' is-complete' : ''}`}>
        <div className="sp-program-modal-head">
          <div>
            {endedAt ? (
              <Tag color="default">Ended early {dayjs(endedAt).format('DD MMM YYYY')}</Tag>
            ) : (
              <Tag color={isComplete ? 'success' : 'processing'}>
                {isComplete ? 'Program complete' : 'Program details'}
              </Tag>
            )}
            <Title level={4} style={{ margin: '8px 0 4px' }}>
              {program.sync_protocol_name || 'Sync program'}
            </Title>
            <Text type="secondary">
              {animalTitle(animal)}
              {animal?.tag_number ? ` · ${animal.tag_number}` : ''}
            </Text>
            <div className="sp-program-detail-meta" style={{ marginTop: 8 }}>
              Started {program.sync_protocol_start_date
                ? dayjs(program.sync_protocol_start_date).format('DD MMM YYYY')
                : '—'}
              {program.insemination_schedule_date ? (
                <> · Timed AI {dayjs(program.insemination_schedule_date).format('DD MMM YYYY')}</>
              ) : null}
              {program.sync_protocol_data?.endReason ? (
                <> · {program.sync_protocol_data.endReason}</>
              ) : null}
            </div>
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {hasOpenSteps && (
                <Button danger icon={<StopOutlined />} onClick={confirmEnd} loading={endMutation.isPending}>
                  End program
                </Button>
              )}
              <Button type="primary" icon={<EditOutlined />} onClick={() => setPanelMode('manage')}>
                Manage program
              </Button>
            </div>
          )}
        </div>

        {completion != null && (
          <div className={`sp-program-detail-progress${isComplete ? ' is-complete' : ''}`}>
            <div className="sp-program-detail-progress-label">
              <Text type="secondary">Completion</Text>
              <Text strong className={isComplete ? 'is-green' : ''}>{completion}%</Text>
            </div>
            <Progress
              percent={completion}
              size="small"
              showInfo={false}
              strokeColor={isComplete ? '#16a34a' : '#1677ff'}
            />
          </div>
        )}

        {program.sync_protocol_data?.steps?.length ? (
          <>
            <SyncProtocolWrittenGuide syncData={program.sync_protocol_data} compact />
            <div className="sp-program-detail-schedule">
              <Text type="secondary" className="sp-program-detail-schedule-label">Schedule overview</Text>
              <SyncProtocolScheduler
                value={program.sync_protocol_data}
                speciesKey={getSpeciesKeyFromAnimal(animal)}
                farmId={animal?.farm_id}
                disabled
              />
            </div>
          </>
        ) : (
          <Empty description="No protocol steps recorded" />
        )}

        {program.notes ? (
          <div className="sp-program-detail-notes">
            <Text type="secondary">Notes</Text>
            <div>{program.notes}</div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderManagePanel = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => saveMutation.mutate(values)}
      className={`bf-form${isComplete ? ' is-complete' : ''}`}
    >
      <div className="sp-program-modal-manage-head">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (mode === 'manage') {
              handleClose();
            } else {
              setPanelMode('view');
              form.resetFields();
            }
          }}
        >
          {mode === 'manage' ? 'Close' : 'Back to details'}
        </Button>
        <div>
          <Text strong>{program?.sync_protocol_name || 'Sync program'}</Text>
          <div className="sp-muted">{animalTitle(animal)}</div>
        </div>
      </div>

      <section className="bf-section bf-section--sync">
        <div className="bf-section-head">
          <h3 className="bf-section-title">Protocol schedule</h3>
          <p className="bf-section-sub">
            Update the hormone protocol, step dates, and field completion status.
          </p>
        </div>
        <div className="bf-section-body">
          <Form.Item
            name="sync_protocol_data"
            label={null}
            rules={[{
              validator: (_, val) => {
                if (!val?.presetId || !val?.steps?.length || !val?.steps[0]?.scheduledDate) {
                  return Promise.reject(new Error('Select a protocol and set the first step start date'));
                }
                return Promise.resolve();
              },
            }]}
          >
            <SyncProtocolScheduler
              speciesKey={getSpeciesKeyFromAnimal(animal)}
              farmId={animal?.farm_id}
              isEditing
            />
          </Form.Item>
        </div>
      </section>

      <section className="bf-section">
        <div className="bf-section-head">
          <h3 className="bf-section-title">Notes (optional)</h3>
        </div>
        <div className="bf-section-body">
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Add any notes about this program..." maxLength={1000} showCount />
          </Form.Item>
        </div>
      </section>

      <div className="bf-form-footer">
        <Button onClick={() => (mode === 'manage' ? handleClose() : setPanelMode('view'))}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
          Update program
        </Button>
      </div>
    </Form>
  );

  return (
    <Modal
      title={
        <div className="sp-sync-modal-header">
          <div className="sp-sync-modal-icon">
            <SyncOutlined />
          </div>
          <div>
            <div className="sp-sync-modal-title">
              {panelMode === 'manage' ? 'Manage sync program' : 'Sync program details'}
            </div>
            <div className="sp-sync-modal-sub">
              {panelMode === 'manage'
                ? 'Update protocol schedule and mark steps done'
                : 'Read-only overview — use Manage to edit the schedule'}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={panelMode === 'manage' ? 1000 : 920}
      destroyOnClose
      className={`professional-breeding-modal sp-program-modal${isComplete ? ' is-complete' : ''}`}
    >
      <Spin spinning={loading}>
        {!loading && panelMode === 'manage' && (program ? renderManagePanel() : <Empty description="Sync program not found" />)}
        {!loading && panelMode === 'view' ? renderViewPanel() : null}
      </Spin>
    </Modal>
  );
}
